const normalizeSearchText = (value) => String(value || '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const buildNameTerms = (category) => {
  const categoryName = String(category?.name || '').trim();
  const parentName = String(category?.parentName || '').trim();

  return [
    categoryName,
    parentName ? `${parentName} ${categoryName}` : '',
  ]
    .map(normalizeSearchText)
    .filter(Boolean);
};

const CATEGORY_ALIASES = {
  'Rent/Mortgage': ['rent', 'mortgage', 'housing', 'home payment', 'lease', 'property'],
  'Car Payment': ['car loan', 'auto loan', 'vehicle loan', 'car finance', 'auto finance'],
  'Car Insurance': ['auto insurance', 'vehicle insurance', 'insurance', 'car policy'],
  'Phone': ['cell', 'cell phone', 'mobile', 'wireless', 'phone bill'],
  'Internet': ['wifi', 'broadband', 'fibre', 'fiber', 'web'],
  'Electricity': ['electric', 'electricity', 'power', 'hydro', 'energy'],
  'Utilities': ['utilities', 'utility', 'heating', 'heat', 'water', 'water bill', 'gas bill', 'gas', 'sewer'],
  'Life Insurance': ['life', 'life insurance', 'term life', 'whole life', 'policy'],
  'Rental Insurance': ['tenant insurance', 'renters insurance', 'rental insurance', 'home insurance'],
  'Daycare': ['childcare', 'child care', 'preschool', 'nursery'],
  'Bank Fees': ['bank fee', 'account fee', 'monthly fee', 'overdraft', 'nsf'],
  'CC Fees & Interest': ['credit card interest', 'interest', 'credit card fee', 'late fee', 'annual fee'],
  'Retirement Savings Fund': ['rrsp', 'retirement', 'pension', '401k', 'ira'],
  'Child Education Fund': ['resp', 'education savings', 'child education', 'college fund', 'school fund'],
  'Line of Credit': ['line of credit', 'loc', 'credit line'],
  'Education Loan': ['student loan', 'school loan', 'education loan', 'tuition loan'],
  'Groceries': ['food', 'groceries', 'grocery', 'supermarket', 'produce'],
  'Restaurants': ['restaurant', 'restaurants', 'dining', 'dining out', 'food', 'meals'],
  'Food Delivery': ['delivery', 'food delivery', 'takeout', 'take out', 'uber eats', 'doordash', 'skip', 'skip the dishes'],
  'Coffee & Snacks': ['coffee', 'snacks', 'cafe', 'bakery', 'tea'],
  'Entertainment': ['fun', 'movies', 'games', 'concerts', 'events'],
  'Clothing': ['clothes', 'clothing', 'apparel', 'fashion', 'shoes'],
  'Personal Care': ['beauty', 'hair', 'skincare', 'barber', 'salon'],
  'Health': ['health', 'pharmacy', 'wellness', 'vision', 'therapy'],
  'Gifts': ['gift', 'gifts', 'present', 'presents'],
  'Gas': ['fuel', 'gas', 'petrol'],
  'Public Transit': ['transit', 'bus', 'subway', 'train', 'metro'],
  'Rideshare': ['uber', 'lyft', 'ride share', 'rideshare', 'cab'],
  'Parking': ['parking', 'garage', 'lot', 'meter'],
  'Shopping & Retail': ['shopping', 'retail', 'general merchandise', 'store'],
  'Home & Household': ['home', 'household', 'furniture', 'decor', 'housewares'],
  'Shipping': ['shipping', 'shipment', 'courier', 'postage', 'mail', 'canada post', 'fedex', 'ups', 'dhl', 'purolator'],
  'Electronics': ['electronics', 'tech', 'computer', 'phone accessories'],
  'Kids': ['kids', 'children', 'child', 'baby', 'babies', 'infant', 'toddler', 'swim school', 'swimming lessons'],
  'Kids & Clothing': ['kids', 'children', 'baby', 'toys', 'toy', 'school clothes', 'kids clothes'],
  'Travel - Airfare': ['airfare', 'flight', 'plane', 'airline'],
  'Travel - Lodging': ['hotel', 'lodging', 'airbnb', 'accommodation'],
  'Travel - Other': ['travel', 'vacation', 'trip', 'tourism'],
  'Medical': ['medical', 'doctor', 'clinic', 'hospital'],
  'Dental': ['dental', 'dentist', 'orthodontist'],
  'Home Repairs': ['repair', 'contractor', 'plumber', 'electrician', 'maintenance'],
  'Auto Repairs': ['mechanic', 'car repair', 'auto repair', 'tires', 'oil change'],
  'Taxes': ['tax', 'taxes', 'property tax', 'income tax'],
  'Education': ['education', 'tuition', 'course', 'school', 'university', 'college'],
  'Donations': ['donation', 'donations', 'charity', 'charitable giving', 'gofundme', 'fundraiser'],
  'Netflix': ['netflix'],
  'Disney+': ['disney', 'disney plus'],
  'Hulu': ['hulu'],
  'Spotify': ['spotify'],
  'Apple Music': ['apple music'],
  'Amazon Prime': ['amazon prime', 'prime'],
  'YouTube Premium': ['youtube', 'youtube premium'],
  'Streaming': ['streaming', 'video streaming'],
  'Music Streaming': ['music streaming', 'tidal', 'pandora'],
  'Software': ['software', 'app', 'apps', 'saas', 'subscription software'],
  'Transfers': ['transfer', 'etransfer', 'e transfer', 'move money'],
  'CC Payments': ['credit card payment', 'card payment', 'visa payment', 'mastercard payment'],
  'Refunds': ['refund', 'refunds', 'return', 'returns'],
};

const buildSearchTerms = (category) => {
  const categoryName = String(category?.name || '').trim();
  const aliases = CATEGORY_ALIASES[categoryName] || [];

  return [
    ...buildNameTerms(category),
    ...aliases,
  ]
    .map(normalizeSearchText)
    .filter(Boolean);
};

const scoreCandidate = (query, category) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return 0;

  const categoryName = normalizeSearchText(category?.name);
  const parentName = normalizeSearchText(category?.parentName);
  const terms = buildSearchTerms(category);

  if (categoryName === normalizedQuery) return 120;
  if (terms.some((term) => term === normalizedQuery)) return 110;
  if (categoryName.startsWith(normalizedQuery)) return 95;
  if (terms.some((term) => term.startsWith(normalizedQuery))) return 88;
  if (terms.some((term) => term.includes(normalizedQuery))) return 78;
  if (normalizedQuery.includes(categoryName) && categoryName) return 74;
  if (parentName && normalizedQuery.includes(parentName)) return 68;

  const queryTokens = normalizedQuery.split(' ');
  const bestOverlap = terms.reduce((best, term) => {
    const tokenMatches = queryTokens.filter((token) => term.includes(token)).length;
    return Math.max(best, tokenMatches);
  }, 0);

  return bestOverlap >= Math.min(2, queryTokens.length) ? 56 + (bestOverlap * 4) : 0;
};

const flattenSectionOptions = (sections = []) => sections.flatMap((section) =>
  (Array.isArray(section.options) ? section.options : []).map((option) => ({
    id: String(option?.id ?? ''),
    name: option?.name || option?.label || '',
    parentName: option?.parentName || section.name || '',
  }))
);

const getDirectCategoryMatches = ({ query, sections = [] }) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  return flattenSectionOptions(sections).filter((option) => {
    const nameTerms = buildNameTerms(option);
    return nameTerms.some((term) => term.includes(normalizedQuery));
  });
};

const hasStrongCategoryMatch = ({ query, sections = [] }) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return false;

  return flattenSectionOptions(sections).some((option) => {
    const nameTerms = buildNameTerms(option);

    return nameTerms.some((term) => (
      term === normalizedQuery ||
      term.startsWith(normalizedQuery) ||
      term.split(' ').some((token) => token.startsWith(normalizedQuery))
    ));
  });
};

const getSimilarCategorySuggestions = ({ query, sections = [] }) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  const candidates = flattenSectionOptions(sections)
    .map((option) => ({ option, score: scoreCandidate(normalizedQuery, option) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.option.name.localeCompare(b.option.name));

  const seen = new Set();
  return candidates.filter(({ option }) => {
    const key = `${option.parentName}:${option.name}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 5).map(({ option }) => option);
};

const hasExactCategoryNameMatch = ({ query, sections = [] }) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return false;
  return flattenSectionOptions(sections).some((option) => normalizeSearchText(option.name) === normalizedQuery);
};

export {
  CATEGORY_ALIASES,
  getDirectCategoryMatches,
  getSimilarCategorySuggestions,
  hasStrongCategoryMatch,
  hasExactCategoryNameMatch,
  normalizeSearchText,
};
